import {
  IconAlertTriangle,
  IconCheck,
  IconChevronDown,
  IconCoins,
  IconDownload,
  IconLoader2,
  IconPhoto,
  IconPlayerPlay,
  IconSelector,
  IconUpload,
  IconX,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { VideoGenerationItem } from '@/api/video-generate';
import { generateTrendVideo } from '@/api/trend-video';
import { VideoResolution } from '@/ai/video-models';
import { authClient } from '@/auth/client';
import { LoginModal } from '@/components/auth/login-modal';
import { CreditsRequiredDialog } from '@/components/pricing/public-credit-packages';
import {
  getTrendCredits,
  resolveTrendVariant,
  TREND_LIST,
  type TrendConfig,
  type TrendScene,
  type TrendVariantOptions,
} from '@/config/trends';
import { useCredits, useInvalidateCredits } from '@/hooks/use-credits';
import {
  useVideoGenerations,
  videoGenerationKeys,
} from '@/hooks/use-video-generation-history';
import { useWorkbenchDraft } from '@/hooks/use-workbench-draft';
import { downloadFile } from '@/lib/download';
import { uploadReferenceImages } from '@/lib/reference-image-upload';
import { Routes } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { TrendPickerDialog } from './trend-picker-dialog';

const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp';
const PHOTO_MAX_BYTES = 10 * 1024 * 1024;
const INSUFFICIENT_CREDITS_PREFIX = 'INSUFFICIENT_CREDITS:';
const RESOLUTION_LABELS: Record<string, string> = {
  [VideoResolution.P480]: '480p',
  [VideoResolution.P720]: '720p HD',
};
const SLOT_LABEL_CLASS =
  'mb-2 font-medium text-[11px] text-white/50 uppercase tracking-[0.14em]';

type MediaTab = 'example' | 'creations';

type SceneSelection =
  | { kind: 'original' }
  | { kind: 'preset'; sceneId: string }
  | { kind: 'upload' };

interface TrendDraft {
  resolution: VideoResolution;
  scene: SceneSelection;
  /** Chosen template of a multi-template effect. */
  variantId?: string;
}

/** Media box classes for the template's frame. */
function frameClass(trend: TrendConfig) {
  return trend.frame === '9:16'
    ? 'mx-auto aspect-[9/16] h-[min(50vh,440px)] max-w-full lg:h-[min(64vh,600px)]'
    : 'aspect-[4/3] w-full';
}

function validatePhoto(file: File) {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
    return 'Choose a JPEG, PNG, or WebP image.';
  }
  if (file.size > PHOTO_MAX_BYTES) return 'Photos must be 10MB or smaller.';
  return undefined;
}

/** Object URLs for a list of files, revoked when the files change. */
function useObjectUrls(files: (File | null)[]) {
  const urls = useMemo(
    () => files.map((file) => (file ? URL.createObjectURL(file) : undefined)),
    [files]
  );
  useEffect(
    () => () => {
      for (const url of urls) if (url) URL.revokeObjectURL(url);
    },
    [urls]
  );
  return urls;
}

function isActive(item: VideoGenerationItem) {
  return (
    item.status === 'pending' ||
    item.status === 'processing' ||
    item.status === 'uploading'
  );
}

/**
 * One card: the original clip (or the visitor's videos) on the left, one
 * upload box per person in the clip on the right, and resolution plus
 * Generate along the bottom.
 */
export function TrendWorkbench({ trend: baseTrend }: { trend: TrendConfig }) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: session } = authClient.useSession();
  const isLoggedIn = Boolean(session?.user?.id);
  const creditsQuery = useCredits();
  const invalidateCredits = useInvalidateCredits();
  const balance = creditsQuery.data?.balance;

  const [variantId, setVariantId] = useState(baseTrend.variants?.defaultId);
  // The effect as one template: the chosen variant merged over the shared
  // settings. Identical to `baseTrend` for single-template effects.
  const trend = useMemo(
    () => resolveTrendVariant(baseTrend, variantId) ?? baseTrend,
    [baseTrend, variantId]
  );
  const [photos, setPhotos] = useState<(File | null)[]>(() =>
    trend.photoSlots.map(() => null)
  );
  const [sceneFile, setSceneFile] = useState<File | null>(null);
  const [scene, setScene] = useState<SceneSelection>({ kind: 'original' });
  const [resolution, setResolution] = useState(trend.defaultResolution);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [creditsDialogOpen, setCreditsDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const [tab, setTab] = useState<MediaTab>('example');
  const [selectedId, setSelectedId] = useState<string>();

  const cost = getTrendCredits(trend, resolution);
  const photoUrls = useObjectUrls(photos);
  const sceneFiles = useMemo(() => [sceneFile], [sceneFile]);
  const [sceneUploadUrl] = useObjectUrls(sceneFiles);
  const missingPhoto = photos.some((photo) => !photo);
  const missingScene = scene.kind === 'upload' && !sceneFile;
  const canGenerate = !missingPhoto && !missingScene && !isSubmitting;

  const draftFiles = useMemo(
    () => ({
      ...Object.fromEntries(
        photos.map((photo, index) => [`photo${index}`, photo])
      ),
      scene: sceneFile,
    }),
    [photos, sceneFile]
  );
  const draftSnapshot = useMemo<TrendDraft>(
    () => ({ resolution, scene, variantId }),
    [resolution, scene, variantId]
  );
  const draft = useWorkbenchDraft<TrendDraft>({
    files: draftFiles,
    key: `trend-${trend.id}`,
    snapshot: draftSnapshot,
    version: 2,
    onRestore: ({ files, payload }) => {
      const restored =
        resolveTrendVariant(baseTrend, payload.variantId) ?? trend;
      if (restored.variants && payload.variantId) {
        setVariantId(payload.variantId);
      }
      setPhotos(
        restored.photoSlots.map((_, index) => files[`photo${index}`] ?? null)
      );
      setSceneFile(files.scene ?? null);
      if (trend.resolutions.includes(payload.resolution)) {
        setResolution(payload.resolution);
      }
      setScene(payload.scene);
    },
  });

  const creations = useVideoGenerations(
    { templateId: trend.id, limit: 12 },
    isLoggedIn
  );
  const selected =
    creations.items.find((item) => item.id === selectedId) ??
    creations.items[0];

  const selectVariant = useCallback(
    (id: string) => {
      const next = resolveTrendVariant(baseTrend, id);
      if (!next) return;
      setError(undefined);
      setVariantId(id);
      setTab('example');
      // Keep photos that still have a slot; variants usually share one.
      setPhotos((current) =>
        next.photoSlots.map((_, index) => current[index] ?? null)
      );
    },
    [baseTrend]
  );

  const setPhoto = useCallback((index: number, file: File | null) => {
    setError(undefined);
    setPhotos((current) =>
      current.map((photo, slot) => (slot === index ? file : photo))
    );
  }, []);

  const onGenerate = async () => {
    if (!canGenerate) return;
    setError(undefined);
    if (!isLoggedIn) {
      await draft.flush();
      setLoginOpen(true);
      return;
    }
    if (typeof balance === 'number' && balance < cost) {
      setCreditsDialogOpen(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const files = [
        ...photos.filter((photo): photo is File => Boolean(photo)),
        ...(scene.kind === 'upload' && sceneFile ? [sceneFile] : []),
      ];
      const { ids } = await uploadReferenceImages(files);
      if (ids.length !== files.length) {
        throw new Error('Photo upload failed. Please try again.');
      }
      const photoFileIds = ids.slice(0, trend.photoSlots.length);
      const result = await generateTrendVideo({
        data: {
          trendId: trend.id,
          variantId: trend.variants ? variantId : undefined,
          photoFileIds,
          resolution: resolution as VideoResolution.P480,
          scene:
            scene.kind === 'upload'
              ? { kind: 'upload', fileId: ids[ids.length - 1] }
              : scene,
        },
      });
      invalidateCredits();
      await queryClient.invalidateQueries({
        queryKey: videoGenerationKeys.all,
      });
      setSelectedId(result.videoId);
      setTab('creations');
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong.';
      if (message.includes(INSUFFICIENT_CREDITS_PREFIX)) {
        invalidateCredits();
        setCreditsDialogOpen(true);
      } else {
        setError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      className="mx-auto max-w-6xl px-4 py-6 lg:px-6"
      data-testid="trend-workbench"
      data-workbench-restored={draft.isRestored ? 'true' : 'false'}
    >
      <div className="overflow-hidden rounded-3xl border border-white/10 bg-[#121212]">
        <div className="flex flex-wrap items-center gap-2 border-white/10 border-b px-4 py-3 sm:px-5">
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="flex min-w-0 items-center gap-2.5 rounded-xl border border-white/10 bg-white/[0.03] py-1.5 pr-3 pl-1.5 text-left transition hover:border-[#8ef25a]/50"
          >
            <img
              src={trend.thumbnail}
              alt=""
              className="h-8 w-11 shrink-0 rounded-md object-cover"
            />
            <span className="min-w-0">
              <span className="block text-[10px] text-white/45 leading-tight">
                Effect
              </span>
              <span className="block truncate font-semibold text-sm leading-tight">
                {trend.name}
              </span>
            </span>
            <IconSelector
              className="size-4 shrink-0 text-white/50"
              aria-hidden
            />
          </button>
          <MediaTabs tab={tab} onChange={setTab} />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
          <div className="min-w-0 p-4 sm:p-5">
            <MediaPanel
              creations={creations.items}
              isLoggedIn={isLoggedIn}
              selected={selected}
              tab={tab}
              trend={trend}
              onLogin={() => setLoginOpen(true)}
              onSelect={setSelectedId}
            />
          </div>

          <div className="flex min-w-0 flex-col gap-5 border-white/10 border-t p-4 sm:p-5 lg:border-t-0 lg:border-l">
            {trend.variants && variantId ? (
              <VariantPicker
                options={trend.variants}
                value={variantId}
                onSelect={selectVariant}
              />
            ) : null}
            <div
              className={cn(
                'grid gap-3',
                trend.photoSlots.length > 1
                  ? 'grid-cols-2'
                  : 'mx-auto w-full max-w-[16rem]'
              )}
            >
              {trend.photoSlots.map((slot, index) => (
                <PhotoSlot
                  key={`${variantId ?? ''}-${slot.id}`}
                  label={slot.label}
                  hint={slot.hint}
                  previewUrl={photoUrls[index]}
                  onChange={(file) => setPhoto(index, file)}
                  onError={setError}
                />
              ))}
            </div>
            {trend.scenes ? (
              <ScenePicker
                options={trend.scenes}
                scene={scene}
                uploadUrl={sceneUploadUrl}
                onSelect={(next) => {
                  setError(undefined);
                  setScene(next);
                }}
                onUpload={(file) => {
                  setSceneFile(file);
                  setScene({ kind: 'upload' });
                }}
                onError={setError}
              />
            ) : null}
          </div>
        </div>

        {error ? (
          <p
            role="alert"
            className="mx-4 mb-3 flex gap-2 rounded-lg border border-red-400/30 bg-red-400/10 px-3 py-2 text-red-100 text-sm sm:mx-5"
          >
            <IconAlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            {error}
          </p>
        ) : null}

        <div className="flex flex-wrap items-center gap-3 border-white/10 border-t px-4 py-3 sm:px-5">
          <label className="relative">
            <span className="sr-only">Resolution</span>
            <select
              value={resolution}
              onChange={(event) =>
                setResolution(event.target.value as VideoResolution)
              }
              className="h-10 cursor-pointer appearance-none rounded-xl border border-white/10 bg-white/[0.04] pr-9 pl-3 font-medium text-sm text-white outline-none transition hover:border-white/25 focus-visible:ring-2 focus-visible:ring-[#8ef25a]/70 [&>option]:bg-[#121212]"
            >
              {trend.resolutions.map((item) => (
                <option key={item} value={item}>
                  {RESOLUTION_LABELS[item]}
                </option>
              ))}
            </select>
            <IconChevronDown
              className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-white/50"
              aria-hidden
            />
          </label>
          <p className="ml-auto text-right text-white/45 text-xs leading-5 tabular-nums">
            {trend.templateSeconds}s · {RESOLUTION_LABELS[resolution]}
            <span className="block text-white/80">{cost} credits</span>
          </p>
          <button
            type="button"
            onClick={() => void onGenerate()}
            disabled={!canGenerate}
            className="flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-[#8ef25a] px-6 font-semibold text-[#071107] transition hover:bg-[#a6ff73] disabled:cursor-not-allowed disabled:bg-[#8ef25a]/25 disabled:text-white/45 sm:w-auto"
          >
            {isSubmitting ? (
              <IconLoader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <IconPlayerPlay className="size-4" aria-hidden />
            )}
            Generate
            <span className="ml-1 inline-flex items-center gap-1 rounded-full bg-black/15 px-2 py-0.5 text-xs">
              <IconCoins className="size-3.5" aria-hidden />
              {cost}
            </span>
          </button>
        </div>
      </div>
      <p className="mt-3 text-center text-white/40 text-xs leading-5">
        By generating, you confirm everyone in your photos agreed to appear in
        the video. Usually ready in 5 to 10 minutes.
      </p>

      <TrendPickerDialog
        current={trend}
        open={pickerOpen}
        trends={TREND_LIST}
        onOpenChange={setPickerOpen}
      />
      <LoginModal
        callbackUrl={trend.path}
        open={loginOpen}
        title="Log in to make your video"
        description="Your photos stay here while you log in."
        onOpenChange={setLoginOpen}
      />
      <CreditsRequiredDialog
        balance={balance}
        cost={cost}
        modelLabel="Seedance 2.5"
        open={creditsDialogOpen}
        returnTo={trend.path}
        toolLabel={trend.name}
        onAdjustSettings={
          resolution === trend.defaultResolution
            ? undefined
            : () => {
                setResolution(trend.defaultResolution);
                setCreditsDialogOpen(false);
              }
        }
        onBeforeCheckout={() => draft.flush()}
        onOpenChange={setCreditsDialogOpen}
        onViewCredits={() =>
          void router.navigate({
            to: Routes.Pricing,
            search: { returnTo: trend.path },
          })
        }
      />
    </section>
  );
}

function FileButton({
  children,
  className,
  onError,
  onFile,
  ...rest
}: {
  children: React.ReactNode;
  className?: string;
  onError: (message: string) => void;
  onFile: (file: File) => void;
} & Omit<React.HTMLAttributes<HTMLButtonElement>, 'onError'>) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <button
        type="button"
        className={className}
        onClick={() => inputRef.current?.click()}
        {...rest}
      >
        {children}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept={PHOTO_ACCEPT}
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (!file) return;
          const problem = validatePhoto(file);
          if (problem) onError(problem);
          else onFile(file);
        }}
      />
    </>
  );
}

function PhotoSlot({
  hint,
  label,
  previewUrl,
  onChange,
  onError,
}: {
  hint: string;
  label: string;
  previewUrl?: string;
  onChange: (file: File | null) => void;
  onError: (message: string) => void;
}) {
  const boxClass =
    'relative flex aspect-[3/4] w-full flex-col items-center justify-center overflow-hidden rounded-2xl';

  return (
    <div className="min-w-0">
      <p className={SLOT_LABEL_CLASS}>{label}</p>
      {previewUrl ? (
        <div className={cn(boxClass, 'border border-white/10 bg-black')}>
          <img
            src={previewUrl}
            alt={label}
            className="size-full object-cover"
          />
          <button
            type="button"
            aria-label={`Remove ${label.toLowerCase()} photo`}
            onClick={() => onChange(null)}
            className="absolute top-2 right-2 grid size-7 place-items-center rounded-full bg-black/70 text-white transition hover:bg-black"
          >
            <IconX className="size-4" aria-hidden />
          </button>
        </div>
      ) : (
        <FileButton
          data-trend-photo-slot
          aria-label={`Upload ${label.toLowerCase()} photo`}
          onFile={onChange}
          onError={onError}
          className={cn(
            boxClass,
            'gap-2 border border-white/20 border-dashed px-3 text-center transition hover:border-[#8ef25a]/60 hover:bg-[#8ef25a]/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8ef25a]/70'
          )}
        >
          <span className="mb-1 grid size-12 place-items-center rounded-full bg-white/[0.07] text-white/70">
            <IconPhoto className="size-5" aria-hidden />
          </span>
          <span className="font-medium text-sm text-white sm:text-base">
            {label}
          </span>
          <span className="text-white/45 text-xs leading-5">{hint}</span>
        </FileButton>
      )}
    </div>
  );
}

function ScenePicker({
  options,
  scene,
  uploadUrl,
  onError,
  onSelect,
  onUpload,
}: {
  options: NonNullable<TrendConfig['scenes']>;
  scene: SceneSelection;
  uploadUrl?: string;
  onError: (message: string) => void;
  onSelect: (scene: SceneSelection) => void;
  onUpload: (file: File) => void;
}) {
  const tileClass = (active: boolean) =>
    cn(
      'relative h-12 w-16 shrink-0 cursor-pointer overflow-hidden rounded-lg border transition has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#8ef25a]/70',
      active
        ? 'border-[#8ef25a] ring-1 ring-[#8ef25a]'
        : 'border-white/10 hover:border-white/30'
    );
  const tiles = [
    {
      key: 'original',
      label: options.originalLabel,
      image: options.originalThumbnail,
      active: scene.kind === 'original',
      select: { kind: 'original' } as SceneSelection,
    },
    ...options.items.map((item: TrendScene) => ({
      key: item.id,
      label: item.label,
      image: item.thumbnail,
      active: scene.kind === 'preset' && scene.sceneId === item.id,
      select: { kind: 'preset', sceneId: item.id } as SceneSelection,
    })),
  ];
  const current =
    scene.kind === 'upload'
      ? options.uploadLabel
      : tiles.find((tile) => tile.active)?.label;

  return (
    <fieldset data-testid="trend-scene-picker" className="min-w-0">
      <legend className={SLOT_LABEL_CLASS}>
        {options.label}
        <span className="ml-2 text-white/80 normal-case tracking-normal">
          {current}
        </span>
      </legend>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {tiles.map((tile) => (
          <label
            key={tile.key}
            title={tile.label}
            className={tileClass(tile.active)}
          >
            <input
              type="radio"
              name="trend-scene"
              value={tile.key}
              checked={tile.active}
              onChange={() => onSelect(tile.select)}
              className="sr-only"
            />
            <span className="sr-only">{tile.label}</span>
            <img src={tile.image} alt="" className="size-full object-cover" />
            {tile.active ? <ActiveMark /> : null}
          </label>
        ))}
        <FileButton
          title="Upload your own background"
          aria-label="Upload your own background"
          aria-pressed={scene.kind === 'upload'}
          onFile={onUpload}
          onError={onError}
          className={cn(
            tileClass(scene.kind === 'upload'),
            'grid place-items-center border-dashed text-white/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8ef25a]/70'
          )}
        >
          {uploadUrl ? (
            <>
              <img
                src={uploadUrl}
                alt=""
                className="absolute inset-0 size-full object-cover"
              />
              {scene.kind === 'upload' ? <ActiveMark /> : null}
            </>
          ) : (
            <IconUpload className="size-4" aria-hidden />
          )}
        </FileButton>
      </div>
      <p className="mt-1.5 text-white/40 text-xs leading-5">{options.hint}</p>
    </fieldset>
  );
}

/** Template chips for a multi-template effect (e.g. solo, group, pet). */
function VariantPicker({
  options,
  value,
  onSelect,
}: {
  options: TrendVariantOptions;
  value: string;
  onSelect: (id: string) => void;
}) {
  return (
    <fieldset data-testid="trend-variant-picker" className="min-w-0">
      <legend className={SLOT_LABEL_CLASS}>{options.label}</legend>
      <div className="grid grid-cols-4 gap-1.5 rounded-xl border border-white/10 bg-white/[0.03] p-1">
        {options.items.map((item) => (
          <label
            key={item.id}
            className={cn(
              'flex h-9 cursor-pointer items-center justify-center rounded-lg px-1 text-center font-medium text-[13px] leading-tight transition has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#8ef25a]/70',
              item.id === value
                ? 'bg-[#8ef25a] text-[#071107]'
                : 'text-white/65 hover:bg-white/[0.06] hover:text-white'
            )}
          >
            <input
              type="radio"
              name="trend-variant"
              value={item.id}
              checked={item.id === value}
              onChange={() => onSelect(item.id)}
              className="sr-only"
            />
            {item.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function ActiveMark() {
  return (
    <IconCheck
      aria-hidden
      className="absolute top-0.5 right-0.5 size-3.5 rounded-full bg-[#8ef25a] p-0.5 text-[#071107]"
    />
  );
}

function MediaTabs({
  tab,
  onChange,
}: {
  tab: MediaTab;
  onChange: (tab: MediaTab) => void;
}) {
  const tabs = [
    ['example', 'Example'],
    ['creations', 'My creations'],
  ] as const;
  return (
    <div className="ml-auto flex items-center gap-1">
      {tabs.map(([value, label]) => (
        <button
          key={value}
          type="button"
          aria-pressed={tab === value}
          onClick={() => onChange(value)}
          className={cn(
            'rounded-lg px-3 py-1.5 text-sm transition',
            tab === value
              ? 'bg-[#8ef25a]/12 text-[#d9ffc6]'
              : 'text-white/55 hover:text-white'
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** The original clip, or the visitor's videos for this effect. */
function MediaPanel({
  creations,
  isLoggedIn,
  selected,
  tab,
  trend,
  onLogin,
  onSelect,
}: {
  creations: VideoGenerationItem[];
  isLoggedIn: boolean;
  selected?: VideoGenerationItem;
  tab: MediaTab;
  trend: TrendConfig;
  onLogin: () => void;
  onSelect: (id: string) => void;
}) {
  if (tab === 'example') {
    return (
      <video
        key={trend.previewVideo}
        src={trend.previewVideo}
        poster={trend.previewPoster}
        autoPlay
        muted
        loop
        playsInline
        controls
        preload="metadata"
        className={cn(frameClass(trend), 'rounded-2xl bg-black object-cover')}
      >
        <track kind="captions" />
      </video>
    );
  }
  if (!isLoggedIn) {
    return (
      <EmptyState trend={trend}>
        <p>Log in to see the videos you made with this effect.</p>
        <button
          type="button"
          onClick={onLogin}
          className="mt-3 rounded-lg border border-white/15 px-4 py-2 text-sm text-white transition hover:border-[#8ef25a]/60"
        >
          Log in
        </button>
      </EmptyState>
    );
  }
  if (!selected) {
    return (
      <EmptyState trend={trend}>
        <p>Your videos for this effect will appear here.</p>
      </EmptyState>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      <GenerationView item={selected} trend={trend} />
      {creations.length > 1 ? (
        <ul className="flex gap-2 overflow-x-auto pb-1">
          {creations.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                aria-pressed={item.id === selected.id}
                aria-label={`Video from ${new Date(item.createdAt).toLocaleString()}`}
                onClick={() => onSelect(item.id)}
                className={cn(
                  'relative block h-14 w-20 overflow-hidden rounded-lg border bg-black',
                  item.id === selected.id
                    ? 'border-[#8ef25a]'
                    : 'border-white/10'
                )}
              >
                {item.startFrame?.url ? (
                  <img
                    src={item.startFrame.url}
                    alt=""
                    className="size-full object-cover opacity-80"
                  />
                ) : null}
                {isActive(item) ? (
                  <IconLoader2 className="absolute inset-0 m-auto size-4 animate-spin text-[#8ef25a]" />
                ) : null}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function EmptyState({
  children,
  trend,
}: {
  children: React.ReactNode;
  trend: TrendConfig;
}) {
  return (
    <div
      className={cn(
        frameClass(trend),
        'flex flex-col items-center justify-center rounded-2xl border border-white/10 border-dashed p-6 text-center text-sm text-white/55'
      )}
    >
      {children}
    </div>
  );
}

function useElapsedSeconds(since: number, running: boolean) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [running]);
  return Math.max(0, Math.floor((now - since) / 1000));
}

function GenerationView({
  item,
  trend,
}: {
  item: VideoGenerationItem;
  trend: TrendConfig;
}) {
  const running = isActive(item);
  const elapsed = useElapsedSeconds(item.createdAt, running);

  if (item.status === 'completed' && item.outputUrl) {
    const url = item.outputUrl;
    return (
      <div className="flex flex-1 flex-col gap-3">
        <video
          key={url}
          src={url}
          controls
          playsInline
          autoPlay
          loop
          className={cn(frameClass(trend), 'rounded-2xl bg-black')}
        >
          <track kind="captions" />
        </video>
        <button
          type="button"
          onClick={() => void downloadFile(url, `${trend.id}-${item.id}.mp4`)}
          className="inline-flex items-center gap-2 self-start rounded-lg bg-[#8ef25a] px-4 py-2 font-semibold text-[#071107] text-sm transition hover:bg-[#a6ff73]"
        >
          <IconDownload className="size-4" aria-hidden />
          Download MP4
        </button>
      </div>
    );
  }

  if (item.status === 'failed') {
    return (
      <EmptyState trend={trend}>
        <IconAlertTriangle className="size-6 text-red-300" aria-hidden />
        <p className="mt-2 text-white/80">This video could not be made.</p>
        {item.errorMessage ? (
          <p className="mt-1 max-w-md text-white/50 text-xs">
            {item.errorMessage}
          </p>
        ) : null}
        <p className="mt-2 text-[#d9ffc6] text-xs">
          Your {item.creditsUsed} credits were returned automatically.
        </p>
      </EmptyState>
    );
  }

  const minutes = Math.floor(elapsed / 60);
  const seconds = String(elapsed % 60).padStart(2, '0');
  return (
    <EmptyState trend={trend}>
      <IconLoader2 className="size-7 animate-spin text-[#8ef25a]" aria-hidden />
      <p className="mt-3 text-white/85">Filming your video…</p>
      <p className="mt-1 text-white/45 text-xs tabular-nums">
        {minutes}:{seconds} elapsed · usually 5 to 10 minutes. You can leave
        this page; the video is saved to your history.
      </p>
    </EmptyState>
  );
}
